import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  Box, Typography, Button, IconButton, Dialog, DialogTitle,
  DialogContent, TextField, Chip, Tooltip, Alert, Snackbar, CircularProgress,
  Divider, Stack, Card, CardContent, CardActions, Autocomplete,
  List, ListItem, ListItemText, ListItemIcon, Tabs, Tab,
  Menu, MenuItem,
} from '@mui/material';
import {
  Add, PictureAsPdf, Draw, History, Delete,
  CheckCircle, AssignmentReturn, Person, Schedule,
  FolderOpen, CloudUpload, Close, Timeline,
  FileDownload, TableChart,
} from '@mui/icons-material';
import * as XLSX from 'xlsx';
import { API_BASE } from '../api';
import { useAuth } from '../contexts/AuthContext';

const fmtDt = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

// ── Signature Pad ─────────────────────────────────────────────────────────────
function SignaturePad({ onConfirm, onCancel, tipo }) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const lastPos = useRef(null);

  const getPos = (e, canvas) => {
    const rect = canvas.getBoundingClientRect();
    const src = e.touches ? e.touches[0] : e;
    return { x: src.clientX - rect.left, y: src.clientY - rect.top };
  };

  const startDraw = (e) => { e.preventDefault(); drawing.current = true; lastPos.current = getPos(e, canvasRef.current); };
  const draw = (e) => {
    e.preventDefault();
    if (!drawing.current) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const pos = getPos(e, canvas);
    ctx.beginPath();
    ctx.moveTo(lastPos.current.x, lastPos.current.y);
    ctx.lineTo(pos.x, pos.y);
    ctx.strokeStyle = '#1a237e'; ctx.lineWidth = 2.5; ctx.lineCap = 'round'; ctx.lineJoin = 'round';
    ctx.stroke();
    lastPos.current = pos;
  };
  const stopDraw = () => { drawing.current = false; };

  const clear = () => canvasRef.current.getContext('2d').clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);

  const confirm = () => {
    const canvas = canvasRef.current;
    const blank = document.createElement('canvas');
    blank.width = canvas.width; blank.height = canvas.height;
    if (canvas.toDataURL() === blank.toDataURL()) { alert('Por favor, assine antes de confirmar.'); return; }
    onConfirm(canvas.toDataURL('image/png'));
  };

  const color = tipo === 'retirada' ? '#b71c1c' : '#1b5e20';
  const label = tipo === 'retirada' ? 'RETIRADA' : 'ENTREGA';

  return (
    <Box>
      <Box sx={{ textAlign: 'center', mb: 1.5 }}>
        <Chip label={label} sx={{ bgcolor: color, color: '#fff', fontWeight: 700, fontSize: '0.9rem', px: 2 }} />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>Assine com o dedo ou mouse na área abaixo</Typography>
      </Box>
      <Box sx={{ border: '2px dashed', borderColor: color, borderRadius: 2, overflow: 'hidden', mb: 1.5, touchAction: 'none' }}>
        <canvas ref={canvasRef} width={440} height={180}
          style={{ display: 'block', width: '100%', height: '180px', cursor: 'crosshair', background: '#fafafa' }}
          onMouseDown={startDraw} onMouseMove={draw} onMouseUp={stopDraw} onMouseLeave={stopDraw}
          onTouchStart={startDraw} onTouchMove={draw} onTouchEnd={stopDraw}
        />
      </Box>
      <Stack direction="row" spacing={1} justifyContent="flex-end">
        <Button onClick={clear} variant="outlined" size="small">Limpar</Button>
        <Button onClick={onCancel} size="small">Cancelar</Button>
        <Button onClick={confirm} variant="contained" sx={{ bgcolor: color }} size="small">
          Confirmar {label.toLowerCase()}
        </Button>
      </Stack>
    </Box>
  );
}

// ── Main Modal ─────────────────────────────────────────────────────────────────
export default function GestaoProjetosModal({ open, onClose }) {
  const { token } = useAuth();
  const headers = React.useMemo(() => token ? { Authorization: `Bearer ${token}` } : {}, [token]);
  const jsonHeaders = React.useMemo(() => ({ ...headers, 'Content-Type': 'application/json' }), [headers]);

  const [tab, setTab] = useState(0);

  // ── Aba Projetos ──
  const [clientes, setClientes] = useState([]);
  const [clienteSel, setClienteSel] = useState(null);
  const [projetos, setProjetos] = useState([]);
  const [loading, setLoading] = useState(false);

  // ── Aba Histórico ──
  const [histGlobal, setHistGlobal] = useState([]);
  const [histGlobalLoading, setHistGlobalLoading] = useState(false);
  const [histFiltroCliente, setHistFiltroCliente] = useState(null);
  const [histDataInicio, setHistDataInicio] = useState('');
  const [histDataFim, setHistDataFim] = useState('');

  // ── Export ──
  const [exportAnchor, setExportAnchor] = useState(null);
  const [exportConfirm, setExportConfirm] = useState({ open: false, tipo: '' });

  // ── Snack ──
  const [snack, setSnack] = useState({ open: false, message: '', severity: 'success' });
  const showSnack = useCallback((message, severity = 'success') => setSnack({ open: true, message, severity }), []);

  // ── Upload ──
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadNome, setUploadNome] = useState('');
  const [uploadDesc, setUploadDesc] = useState('');
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  // ── Assinatura ──
  const [sigOpen, setSigOpen] = useState(false);
  const [sigTipo, setSigTipo] = useState('retirada');
  const [sigProjeto, setSigProjeto] = useState(null);
  const [sigObs, setSigObs] = useState('');
  const [sigSaving, setSigSaving] = useState(false);

  // ── Histórico por projeto ──
  const [histOpen, setHistOpen] = useState(false);
  const [histProjeto, setHistProjeto] = useState(null);
  const [histData, setHistData] = useState([]);
  const [histLoading, setHistLoading] = useState(false);

  // ── Ver assinatura ──
  const [viewSigOpen, setViewSigOpen] = useState(false);
  const [viewSigData, setViewSigData] = useState(null);

  // Carregar clientes quando abre
  useEffect(() => {
    if (!open) return;
    fetch(`${API_BASE}/clientes`, { headers })
      .then(r => r.json())
      .then(d => setClientes(Array.isArray(d) ? d : (d.clientes || [])))
      .catch(() => {});
  }, [open, headers]);

  // Carregar histórico global quando muda para aba histórico
  const loadHistGlobal = useCallback(async (clienteId) => {
    setHistGlobalLoading(true);
    try {
      const url = clienteId
        ? `${API_BASE}/gestao-projetos/historico?cliente_id=${clienteId}`
        : `${API_BASE}/gestao-projetos/historico`;
      const r = await fetch(url, { headers });
      const d = await r.json();
      setHistGlobal(Array.isArray(d) ? d : []);
    } catch { setHistGlobal([]); }
    finally { setHistGlobalLoading(false); }
  }, [headers]);

  useEffect(() => {
    if (open && tab === 1) loadHistGlobal(histFiltroCliente?.id);
  }, [open, tab, histFiltroCliente, loadHistGlobal]);

  // Histórico filtrado por intervalo de datas (client-side)
  const histGlobalFiltered = useMemo(() => {
    let list = histGlobal;
    if (histDataInicio) {
      const start = new Date(histDataInicio + 'T00:00:00');
      list = list.filter(m => new Date(m.data_movimentacao) >= start);
    }
    if (histDataFim) {
      const end = new Date(histDataFim + 'T23:59:59');
      list = list.filter(m => new Date(m.data_movimentacao) <= end);
    }
    return list;
  }, [histGlobal, histDataInicio, histDataFim]);

  const temFiltros = !!(histFiltroCliente || histDataInicio || histDataFim);

  const doExport = useCallback((tipo) => {
    const data = histGlobalFiltered;
    if (tipo === 'xlsx') {
      const rows = data.map(m => ({
        'Tipo': m.tipo === 'retirada' ? 'RETIRADA' : 'ENTREGA',
        'Projeto': m.projeto_nome || '',
        'Cliente': clienteNome(m.cliente_id),
        'Data/Hora': fmtDt(m.data_movimentacao),
        'Responsável': m.usuario_nome || '',
        'Observação': m.observacao || '',
        'Assinatura': m.assinatura_base64 ? 'Sim' : 'Não',
      }));
      const ws = XLSX.utils.json_to_sheet(rows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Histórico');
      XLSX.writeFile(wb, 'historico-projetos.xlsx');
    } else {
      const periodoLabel = (histDataInicio || histDataFim)
        ? ` · ${histDataInicio ? 'De ' + histDataInicio.split('-').reverse().join('/') : ''} ${histDataFim ? 'Até ' + histDataFim.split('-').reverse().join('/') : ''}`.trim()
        : '';
      const clienteLabel = histFiltroCliente ? ` · ${histFiltroCliente.nome}` : '';
      const html = `<html><head><meta charset="utf-8"><title>Histórico de Projetos</title>
        <style>
          body{font-family:Arial,sans-serif;font-size:11px;padding:16px}
          h2{color:#1565c0;margin:0 0 4px}p{color:#666;font-size:10px;margin:0 0 12px}
          table{width:100%;border-collapse:collapse}
          th{background:#1565c0;color:#fff;padding:6px 8px;text-align:left;font-size:10px;font-weight:700;text-transform:uppercase}
          td{padding:5px 8px;border-bottom:1px solid #e0e0e0;font-size:10px}
          tr:nth-child(even){background:#f9f9f9}
          .ret{color:#c62828;font-weight:700}.ent{color:#2e7d32;font-weight:700}
        </style></head><body>
        <h2>Histórico de Movimentações — Gestão de Projetos</h2>
        <p>Gerado em ${new Date().toLocaleString('pt-BR')}${clienteLabel}${periodoLabel} · ${data.length} registro(s)</p>
        <table><thead><tr>
          <th>Tipo</th><th>Projeto</th><th>Cliente</th><th>Data/Hora</th><th>Responsável</th><th>Observação</th><th>Assinatura</th>
        </tr></thead><tbody>
          ${data.map(m => `<tr>
            <td class="${m.tipo === 'retirada' ? 'ret' : 'ent'}">${m.tipo === 'retirada' ? 'RETIRADA' : 'ENTREGA'}</td>
            <td>${m.projeto_nome || ''}</td>
            <td>${clienteNome(m.cliente_id)}</td>
            <td>${fmtDt(m.data_movimentacao)}</td>
            <td>${m.usuario_nome || ''}</td>
            <td>${m.observacao || ''}</td>
            <td>${m.assinatura_base64 ? 'Sim' : 'Não'}</td>
          </tr>`).join('')}
        </tbody></table></body></html>`;
      const w = window.open('', '_blank', 'width=900,height=600');
      w.document.write(html);
      w.document.close();
      setTimeout(() => w.print(), 400);
    }
  }, [histGlobalFiltered, histFiltroCliente, histDataInicio, histDataFim]); // eslint-disable-line

  const handleExportClick = (tipo) => {
    setExportAnchor(null);
    if (!temFiltros) {
      setExportConfirm({ open: true, tipo });
    } else {
      doExport(tipo);
    }
  };

  const loadProjetos = useCallback(async (clienteId) => {
    if (!clienteId) { setProjetos([]); return; }
    setLoading(true);
    try {
      const r = await fetch(`${API_BASE}/gestao-projetos?cliente_id=${clienteId}`, { headers });
      const d = await r.json();
      setProjetos(Array.isArray(d) ? d : []);
    } catch { showSnack('Erro ao carregar projetos', 'error'); }
    finally { setLoading(false); }
  }, [headers, showSnack]);

  useEffect(() => { loadProjetos(clienteSel?.id); }, [clienteSel, loadProjetos]);

  const handleClose = () => {
    setClienteSel(null); setProjetos([]); setUploadOpen(false);
    setSigOpen(false); setHistOpen(false); setViewSigOpen(false);
    setTab(0); setHistFiltroCliente(null); setHistGlobal([]);
    setHistDataInicio(''); setHistDataFim('');
    setExportAnchor(null); setExportConfirm({ open: false, tipo: '' });
    onClose();
  };

  const handleUpload = async () => {
    if (!uploadNome.trim() || !clienteSel) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('nome', uploadNome.trim());
      fd.append('descricao', uploadDesc.trim());
      fd.append('cliente_id', clienteSel.id);
      if (uploadFile) fd.append('pdf', uploadFile);
      const r = await fetch(`${API_BASE}/gestao-projetos`, { method: 'POST', headers, body: fd });
      if (!r.ok) throw new Error();
      setUploadOpen(false); setUploadNome(''); setUploadDesc(''); setUploadFile(null);
      await loadProjetos(clienteSel.id);
      showSnack('Projeto adicionado!');
    } catch { showSnack('Erro ao adicionar projeto', 'error'); }
    finally { setUploading(false); }
  };

  const openSig = (projeto, tipo) => { setSigProjeto(projeto); setSigTipo(tipo); setSigObs(''); setSigOpen(true); };

  const handleSigConfirm = async (assinaturaBase64) => {
    setSigSaving(true);
    try {
      const r = await fetch(`${API_BASE}/gestao-projetos/${sigProjeto.id}/movimentacoes`, {
        method: 'POST', headers: jsonHeaders,
        body: JSON.stringify({ tipo: sigTipo, assinatura_base64: assinaturaBase64, observacao: sigObs }),
      });
      if (!r.ok) {
        const errData = await r.json().catch(() => ({}));
        throw new Error(errData?.errors?.join(', ') || errData?.error || `Erro ${r.status}`);
      }
      setSigOpen(false);
      await loadProjetos(clienteSel.id);
      if (tab === 1) loadHistGlobal(histFiltroCliente?.id);
      showSnack(`${sigTipo === 'retirada' ? 'Retirada' : 'Entrega'} registrada!`);
    } catch (e) { showSnack(`Erro ao registrar movimentação: ${e.message}`, 'error'); }
    finally { setSigSaving(false); }
  };

  const openHistProjeto = async (projeto) => {
    setHistProjeto(projeto); setHistOpen(true); setHistLoading(true);
    try {
      const r = await fetch(`${API_BASE}/gestao-projetos/${projeto.id}/movimentacoes`, { headers });
      const d = await r.json();
      setHistData(Array.isArray(d) ? d : []);
    } catch { setHistData([]); }
    finally { setHistLoading(false); }
  };

  const handleDelete = async (projeto) => {
    if (!window.confirm(`Excluir o projeto "${projeto.nome}"?`)) return;
    try {
      await fetch(`${API_BASE}/gestao-projetos/${projeto.id}`, { method: 'DELETE', headers });
      await loadProjetos(clienteSel.id);
      showSnack('Projeto excluído');
    } catch { showSnack('Erro ao excluir', 'error'); }
  };

  const ultimaMovBadge = (proj) => {
    const m = proj.ultima_movimentacao;
    if (!m) return null;
    const isRet = m.tipo === 'retirada';
    return (
      <Chip size="small"
        icon={isRet ? <AssignmentReturn sx={{ fontSize: '0.75rem !important' }} /> : <CheckCircle sx={{ fontSize: '0.75rem !important' }} />}
        label={`${isRet ? 'Retirado' : 'Entregue'} ${fmtDt(m.data_movimentacao)}`}
        color={isRet ? 'error' : 'success'} variant="outlined" sx={{ fontSize: '0.68rem' }}
      />
    );
  };

  // Nome do cliente a partir do id
  const clienteNome = (id) => clientes.find(c => c.id === id)?.nome || `Cliente #${id}`;

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth PaperProps={{ sx: { maxHeight: '90vh' } }}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 0 }}>
          <FolderOpen sx={{ color: '#1565c0' }} />
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" fontWeight={700}>Gestão de Projetos</Typography>
          </Box>
          <IconButton onClick={handleClose} size="small"><Close /></IconButton>
        </DialogTitle>

        {/* Abas */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}>
          <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ minHeight: 40 }}>
            <Tab label="Projetos" icon={<FolderOpen sx={{ fontSize: 16 }} />} iconPosition="start"
              sx={{ minHeight: 40, fontSize: '0.8rem', py: 0.5 }} />
            <Tab label="Histórico" icon={<Timeline sx={{ fontSize: 16 }} />} iconPosition="start"
              sx={{ minHeight: 40, fontSize: '0.8rem', py: 0.5 }} />
          </Tabs>
        </Box>

        <DialogContent sx={{ p: 2, overflowY: 'auto' }}>

          {/* ─── ABA PROJETOS ─── */}
          {tab === 0 && (
            <Box>
              <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                <Autocomplete
                  options={clientes}
                  getOptionLabel={o => o?.nome || ''}
                  isOptionEqualToValue={(o, v) => o?.id === v?.id}
                  value={clienteSel}
                  onChange={(_, v) => setClienteSel(v)}
                  renderInput={params => <TextField {...params} label="Selecionar cliente" size="small" />}
                  sx={{ minWidth: 260, flex: 1 }}
                />
                {clienteSel && (
                  <Button variant="contained" startIcon={<Add />} onClick={() => setUploadOpen(true)} size="small">
                    Adicionar projeto
                  </Button>
                )}
              </Box>

              {!clienteSel && (
                <Box sx={{ textAlign: 'center', py: 5, color: 'text.secondary' }}>
                  <FolderOpen sx={{ fontSize: 56, opacity: 0.2 }} />
                  <Typography>Selecione um cliente para ver os projetos.</Typography>
                </Box>
              )}

              {loading && <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress /></Box>}

              {!loading && clienteSel && projetos.length === 0 && (
                <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
                  <FolderOpen sx={{ fontSize: 56, opacity: 0.3 }} />
                  <Typography>Nenhum projeto cadastrado para este cliente.</Typography>
                  <Button variant="outlined" sx={{ mt: 1.5 }} startIcon={<Add />} size="small" onClick={() => setUploadOpen(true)}>
                    Adicionar primeiro projeto
                  </Button>
                </Box>
              )}

              {!loading && projetos.length > 0 && (
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' }, gap: 1.5 }}>
                  {projetos.map(proj => (
                    <Card key={proj.id} variant="outlined" sx={{ borderRadius: 2 }}>
                      <CardContent sx={{ pb: 0, pt: 1.5, px: 1.5 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
                          <Typography fontWeight={700} sx={{ flex: 1, mr: 0.5, fontSize: '0.85rem', lineHeight: 1.3 }}>{proj.nome}</Typography>
                          <Tooltip title="Excluir">
                            <IconButton size="small" color="error" onClick={() => handleDelete(proj)} sx={{ mt: -0.5 }}>
                              <Delete sx={{ fontSize: 15 }} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                        {proj.descricao && (
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5, fontSize: '0.75rem' }}>{proj.descricao}</Typography>
                        )}
                        {proj.pdf_path ? (
                          <Button size="small" startIcon={<PictureAsPdf />} href={proj.pdf_path} target="_blank"
                            variant="outlined" color="error" sx={{ mb: 0.5, fontSize: '0.7rem', py: 0.2 }}>
                            {proj.pdf_nome || 'Ver PDF'}
                          </Button>
                        ) : (
                          <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mb: 0.5 }}>Sem PDF</Typography>
                        )}
                        <Box sx={{ mb: 0.5 }}>{ultimaMovBadge(proj)}</Box>
                      </CardContent>
                      <CardActions sx={{ pt: 0, px: 1, gap: 0.5, flexWrap: 'wrap' }}>
                        <Button size="small" variant="contained" color="error"
                          startIcon={<AssignmentReturn sx={{ fontSize: 13 }} />}
                          onClick={() => openSig(proj, 'retirada')} sx={{ fontSize: '0.7rem' }}>
                          Retirar
                        </Button>
                        <Button size="small" variant="contained" color="success"
                          startIcon={<CheckCircle sx={{ fontSize: 13 }} />}
                          onClick={() => openSig(proj, 'entrega')} sx={{ fontSize: '0.7rem' }}>
                          Entregar
                        </Button>
                        <Tooltip title="Histórico do projeto">
                          <IconButton size="small" onClick={() => openHistProjeto(proj)}>
                            <History sx={{ fontSize: 15 }} />
                          </IconButton>
                        </Tooltip>
                      </CardActions>
                    </Card>
                  ))}
                </Box>
              )}
            </Box>
          )}

          {/* ─── ABA HISTÓRICO ─── */}
          {tab === 1 && (
            <Box>
              {/* Filtros */}
              <Box sx={{ display: 'flex', gap: 1.5, mb: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
                <Autocomplete
                  options={clientes}
                  getOptionLabel={o => o?.nome || ''}
                  isOptionEqualToValue={(o, v) => o?.id === v?.id}
                  value={histFiltroCliente}
                  onChange={(_, v) => setHistFiltroCliente(v)}
                  renderInput={params => <TextField {...params} label="Filtrar por cliente (opcional)" size="small" />}
                  sx={{ minWidth: 220, flex: 1 }}
                />
                <TextField label="De" type="date" size="small" value={histDataInicio}
                  onChange={e => setHistDataInicio(e.target.value)}
                  InputLabelProps={{ shrink: true }} sx={{ width: 148 }} />
                <TextField label="Até" type="date" size="small" value={histDataFim}
                  onChange={e => setHistDataFim(e.target.value)}
                  InputLabelProps={{ shrink: true }} sx={{ width: 148 }} />
                <Button size="small" variant="outlined" onClick={() => loadHistGlobal(histFiltroCliente?.id)}>
                  Atualizar
                </Button>
                <Button size="small" variant="contained" color="success"
                  startIcon={<FileDownload sx={{ fontSize: 16 }} />}
                  onClick={e => setExportAnchor(e.currentTarget)}
                  disabled={histGlobal.length === 0}>
                  Exportar
                </Button>
                <Menu anchorEl={exportAnchor} open={Boolean(exportAnchor)} onClose={() => setExportAnchor(null)}>
                  <MenuItem onClick={() => handleExportClick('xlsx')} sx={{ gap: 1 }}>
                    <TableChart sx={{ fontSize: 18, color: '#2e7d32' }} /> Excel (.xlsx)
                  </MenuItem>
                  <MenuItem onClick={() => handleExportClick('pdf')} sx={{ gap: 1 }}>
                    <PictureAsPdf sx={{ fontSize: 18, color: '#c62828' }} /> PDF (imprimir)
                  </MenuItem>
                </Menu>
              </Box>
              {/* Info de registros */}
              {!histGlobalLoading && histGlobal.length > 0 && (
                <Box sx={{ mb: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    {histGlobalFiltered.length} de {histGlobal.length} movimentação(ões)
                    {temFiltros ? ' (filtrado)' : ''}
                  </Typography>
                </Box>
              )}

              {histGlobalLoading && <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress /></Box>}

              {!histGlobalLoading && histGlobalFiltered.length === 0 && (
                <Box sx={{ textAlign: 'center', py: 5, color: 'text.secondary' }}>
                  <Timeline sx={{ fontSize: 56, opacity: 0.2 }} />
                  <Typography>{histGlobal.length > 0 ? 'Nenhuma movimentação no período selecionado.' : 'Nenhuma movimentação registrada.'}</Typography>
                </Box>
              )}

              {!histGlobalLoading && histGlobalFiltered.length > 0 && (
                <List disablePadding>
                  {histGlobalFiltered.map((m, i) => {
                    const isRet = m.tipo === 'retirada';
                    const color = isRet ? '#c62828' : '#2e7d32';
                    return (
                      <React.Fragment key={m.id}>
                        {i > 0 && <Divider />}
                        <ListItem alignItems="flex-start" sx={{ px: 1, py: 1 }}>
                          <ListItemIcon sx={{ minWidth: 36, mt: 0.5 }}>
                            {isRet
                              ? <AssignmentReturn sx={{ color, fontSize: 20 }} />
                              : <CheckCircle sx={{ color, fontSize: 20 }} />}
                          </ListItemIcon>
                          <ListItemText
                            primary={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap', mb: 0.3 }}>
                                <Chip size="small" label={isRet ? 'RETIRADA' : 'ENTREGA'}
                                  color={isRet ? 'error' : 'success'} sx={{ fontWeight: 700, fontSize: '0.65rem' }} />
                                <Typography variant="body2" fontWeight={700} sx={{ fontSize: '0.82rem' }}>
                                  {m.projeto_nome}
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                                  — {clienteNome(m.cliente_id)}
                                </Typography>
                              </Box>
                            }
                            secondary={
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                                  <Schedule sx={{ fontSize: 12, color: 'text.secondary' }} />
                                  <Typography variant="caption" color="text.secondary">{fmtDt(m.data_movimentacao)}</Typography>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                                  <Person sx={{ fontSize: 12, color: 'text.secondary' }} />
                                  <Typography variant="caption" color="text.secondary">{m.usuario_nome || '—'}</Typography>
                                </Box>
                                {m.observacao && (
                                  <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                    "{m.observacao}"
                                  </Typography>
                                )}
                                {m.assinatura_base64 && (
                                  <Button size="small" startIcon={<Draw sx={{ fontSize: 11 }} />}
                                    onClick={() => { setViewSigData(m); setViewSigOpen(true); }}
                                    sx={{ fontSize: '0.68rem', p: '1px 6px' }}>
                                    Ver assinatura
                                  </Button>
                                )}
                              </Box>
                            }
                          />
                        </ListItem>
                      </React.Fragment>
                    );
                  })}
                </List>
              )}
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* ── Sub-modal: Adicionar projeto ── */}
      <Dialog open={uploadOpen} onClose={() => setUploadOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Adicionar projeto</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, pt: 1.5 }}>
          <TextField label="Nome do projeto *" value={uploadNome} onChange={e => setUploadNome(e.target.value)} fullWidth size="small" />
          <TextField label="Descrição" value={uploadDesc} onChange={e => setUploadDesc(e.target.value)} fullWidth size="small" multiline rows={2} />
          <Button variant="outlined" component="label" startIcon={<CloudUpload />} fullWidth size="small">
            {uploadFile ? uploadFile.name : 'Selecionar PDF'}
            <input hidden type="file" accept="application/pdf" onChange={e => setUploadFile(e.target.files?.[0] || null)} />
          </Button>
        </DialogContent>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, p: 2, pt: 0 }}>
          <Button onClick={() => setUploadOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleUpload} disabled={!uploadNome.trim() || uploading}
            startIcon={uploading ? <CircularProgress size={14} color="inherit" /> : <Add />}>
            {uploading ? 'Salvando...' : 'Adicionar'}
          </Button>
        </Box>
      </Dialog>

      {/* ── Sub-modal: Assinatura ── */}
      <Dialog open={sigOpen} onClose={() => !sigSaving && setSigOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ pb: 0.5 }}>
          {sigTipo === 'retirada' ? 'Retirada' : 'Entrega'} — {sigProjeto?.nome}
          <IconButton onClick={() => setSigOpen(false)} sx={{ position: 'absolute', right: 8, top: 8 }} size="small"><Close /></IconButton>
        </DialogTitle>
        <DialogContent>
          <TextField label="Observação (opcional)" value={sigObs} onChange={e => setSigObs(e.target.value)}
            fullWidth size="small" multiline rows={2} sx={{ mb: 1.5, mt: 0.5 }} />
          {sigSaving
            ? <Box sx={{ textAlign: 'center', py: 3 }}><CircularProgress /><Typography sx={{ mt: 1 }} variant="body2">Salvando...</Typography></Box>
            : <SignaturePad tipo={sigTipo} onConfirm={handleSigConfirm} onCancel={() => setSigOpen(false)} />}
        </DialogContent>
      </Dialog>

      {/* ── Sub-modal: Histórico por projeto ── */}
      <Dialog open={histOpen} onClose={() => setHistOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>
          Histórico — {histProjeto?.nome}
          <IconButton onClick={() => setHistOpen(false)} sx={{ position: 'absolute', right: 8, top: 8 }} size="small"><Close /></IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          {histLoading && <Box sx={{ textAlign: 'center', py: 3 }}><CircularProgress /></Box>}
          {!histLoading && histData.length === 0 && (
            <Typography color="text.secondary" sx={{ textAlign: 'center', py: 3, fontSize: '0.85rem' }}>Nenhuma movimentação registrada.</Typography>
          )}
          <List disablePadding>
            {histData.map((m, i) => {
              const isRet = m.tipo === 'retirada';
              return (
                <React.Fragment key={m.id}>
                  {i > 0 && <Divider />}
                  <ListItem alignItems="flex-start" sx={{ px: 2, py: 1 }}>
                    <ListItemIcon sx={{ minWidth: 32, mt: 0.5 }}>
                      {isRet ? <AssignmentReturn sx={{ color: '#c62828', fontSize: 18 }} /> : <CheckCircle sx={{ color: '#2e7d32', fontSize: 18 }} />}
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                          <Chip size="small" label={isRet ? 'RETIRADA' : 'ENTREGA'} color={isRet ? 'error' : 'success'} sx={{ fontWeight: 700, fontSize: '0.65rem' }} />
                          <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.8rem' }}>{m.usuario_nome || '—'}</Typography>
                        </Box>
                      }
                      secondary={
                        <Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.3 }}>
                            <Schedule sx={{ fontSize: 12, color: 'text.secondary' }} />
                            <Typography variant="caption" color="text.secondary">{fmtDt(m.data_movimentacao)}</Typography>
                          </Box>
                          {m.observacao && <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{m.observacao}</Typography>}
                          {m.assinatura_base64 && (
                            <Button size="small" startIcon={<Draw sx={{ fontSize: 12 }} />}
                              onClick={() => { setViewSigData(m); setViewSigOpen(true); }}
                              sx={{ fontSize: '0.68rem', p: '2px 6px', mt: 0.3 }}>
                              Ver assinatura
                            </Button>
                          )}
                        </Box>
                      }
                    />
                  </ListItem>
                </React.Fragment>
              );
            })}
          </List>
        </DialogContent>
      </Dialog>

      {/* ── Sub-modal: Ver assinatura ── */}
      <Dialog open={viewSigOpen} onClose={() => setViewSigOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ pb: 0.5 }}>
          Assinatura
          <IconButton onClick={() => setViewSigOpen(false)} sx={{ position: 'absolute', right: 8, top: 8 }} size="small"><Close /></IconButton>
        </DialogTitle>
        <DialogContent>
          {viewSigData && (
            <Box>
              <Box sx={{ display: 'flex', gap: 0.5, mb: 1, flexWrap: 'wrap' }}>
                <Chip size="small" icon={<Person sx={{ fontSize: '0.75rem !important' }} />} label={viewSigData.usuario_nome || '—'} />
                <Chip size="small" icon={<Schedule sx={{ fontSize: '0.75rem !important' }} />} label={fmtDt(viewSigData.data_movimentacao)} />
              </Box>
              <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
                <img src={viewSigData.assinatura_base64} alt="Assinatura" style={{ width: '100%', display: 'block', background: '#fafafa' }} />
              </Box>
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* ── Confirmação: exportar toda a listagem ── */}
      <Dialog open={exportConfirm.open} onClose={() => setExportConfirm({ open: false, tipo: '' })} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>Exportar toda a listagem?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
            Nenhum filtro aplicado. Serão exportadas <strong>{histGlobal.length}</strong> movimentações de todos os clientes e períodos.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Deseja continuar?
          </Typography>
          <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 2.5 }}>
            <Button onClick={() => setExportConfirm({ open: false, tipo: '' })}>Cancelar</Button>
            <Button variant="contained" color="success"
              startIcon={exportConfirm.tipo === 'xlsx' ? <TableChart /> : <PictureAsPdf />}
              onClick={() => { setExportConfirm({ open: false, tipo: '' }); doExport(exportConfirm.tipo); }}>
              Confirmar e exportar
            </Button>
          </Stack>
        </DialogContent>
      </Dialog>

      <Snackbar open={snack.open} autoHideDuration={4000} onClose={() => setSnack(s => ({ ...s, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={snack.severity} onClose={() => setSnack(s => ({ ...s, open: false }))}>{snack.message}</Alert>
      </Snackbar>
    </>
  );
}
